import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type { AlbumFilters } from "../redux/album.types.ts"
import type {
  Album,
  AlbumCreateData,
  AlbumFilterOptions,
  AlbumListItem,
  AlbumUpdateData
} from "../types/album.types.ts"
import type {
  AlbumAttachmentsData,
  AlbumId,
  AlbumListParams
} from "./services.types.ts"

const BASE_URL = `/project-management/albums`

const PATHS = {
  // Albums
  list: `${BASE_URL}/`,
  create: `${BASE_URL}/`,
  read: (id: AlbumId) => `${BASE_URL}/${id}/`,
  update: (id: AlbumId) => `${BASE_URL}/${id}/`,
  partialUpdate: (id: AlbumId) => `${BASE_URL}/${id}/`,
  delete: (id: AlbumId) => `${BASE_URL}/${id}/`,
  addAttachments: (id: AlbumId) => `${BASE_URL}/${id}/add-attachments/`,
  removeAttachments: (id: AlbumId) => `${BASE_URL}/${id}/remove-attachments/`,
  getAlbumFilterOptions: `${BASE_URL}/filter_options/`
}

// GET /project-management/albums/
export const getAlbums = async (params?: AlbumListParams) => {
  return request.api.get<
    BasePagination<AlbumListItem>,
    BaseApiResponse<unknown>
  >(PATHS.list, params)
}

// POST /project-management/albums/
export const createAlbum = async (data: AlbumCreateData) => {
  return request.api.post<
    BaseApiResponse<AlbumListItem>,
    BaseApiResponse<unknown>
  >(PATHS.create, data)
}

// GET /project-management/albums/{id}/
export const getAlbum = async (id: AlbumId, params?: Partial<AlbumFilters>) => {
  return request.api.get<Album, BaseApiResponse<unknown>>(
    PATHS.read(id),
    params
  )
}

// PUT /project-management/albums/{id}/
export const updateAlbum = async (id: AlbumId, data: AlbumUpdateData) => {
  return request.api.put<BaseApiResponse<Album>, BaseApiResponse<unknown>>(
    PATHS.update(id),
    data
  )
}

// PATCH /project-management/albums/{id}/
export const patchAlbum = async (
  id: AlbumId,
  data: Partial<AlbumUpdateData>
) => {
  return request.api.patch<BaseApiResponse<Album>, BaseApiResponse<unknown>>(
    PATHS.partialUpdate(id),
    data
  )
}

// DELETE /project-management/albums/{id}/
export const deleteAlbum = async (id: AlbumId) => {
  return request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.delete(id)
  )
}

// POST /project-management/albums/{id}/add-attachments/
export const addAlbumAttachments = async (
  id: AlbumId,
  data: AlbumAttachmentsData
) => {
  return request.api.post<BaseApiResponse<Album>, BaseApiResponse<unknown>>(
    PATHS.addAttachments(id),
    data
  )
}

// POST /project-management/albums/{id}/remove-attachments/
export const removeAlbumAttachments = async (
  id: AlbumId,
  data: AlbumAttachmentsData
) => {
  return request.api.post<BaseApiResponse<Album>, BaseApiResponse<unknown>>(
    PATHS.removeAttachments(id),
    data
  )
}

// GET /project-management/albums/{id}/filter_options/
export const getAlbumFilterOptions = async (album: AlbumId) => {
  return request.api.get<AlbumFilterOptions, BaseApiResponse<unknown>>(
    PATHS.getAlbumFilterOptions,
    { album }
  )
}
